import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getClientSessionApi,
  lookupClientApi,
  setApplicantEmailApi,
} from "../api/application";

/**
 * Resolved client (IDV-4).
 *
 * The applicant enters one code. They are not expected to know whether it
 * identifies their organisation or a specific issuer within it, so the server
 * decides:
 *
 *   • an issuer code resolves straight to a client, and capture can begin
 *   • a tenant code returns that organisation's issuers to choose from, and
 *     choosing one is simply another lookup with its code
 *
 * The accepted-document list here drives the picker only. The same list is held
 * server-side and enforced in the validation path, because a public client cannot
 * be trusted to constrain itself.
 */

export interface AcceptedDocument {
  documentKey: string;
  name: string;
  /** The verification engine's own document type. */
  engineType?: string;
}

export interface IssuerOption {
  issuerCode: string;
  issuerName: string;
}

export interface ResolvedClient {
  displayName: string;
  acceptedDocuments: AcceptedDocument[];
  enableAdditionalDocuments: boolean;
  branding?: { logoUrl: string | null; colorScheme: string | null };
}

/** Set when the code named an organisation rather than an issuer. */
export interface IssuerChoice {
  tenantName: string;
  issuers: IssuerOption[];
}

interface ClientContextType {
  client: ResolvedClient | null;
  /** Non-null while the applicant still has an issuer to pick. */
  issuerChoice: IssuerChoice | null;
  isResolving: boolean;
  error: string | null;
  /** Resolves a tenant code, issuer code or client id. */
  lookupCode: (code: string) => Promise<void>;
  /** Abandons a pending issuer choice and returns to the code entry screen. */
  cancelIssuerChoice: () => void;
  /** Contact address, once given. Held server-side; this is the local copy. */
  applicantEmail: string | null;
  /** Records the address. Resolves true when the server accepted it. */
  submitEmail: (email: string) => Promise<boolean>;
  /**
   * Forgets the resolved client, so the landing page asks for a code again.
   *
   * This provider sits above the router outlet and so never remounts on
   * navigation: without an explicit reset, a finished journey returns to a
   * landing page that still believes a client is resolved and asks for an email
   * address instead of a code.
   */
  reset: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const [client, setClient] = useState<ResolvedClient | null>(null);
  const [issuerChoice, setIssuerChoice] = useState<IssuerChoice | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applicantEmail, setApplicantEmail] = useState<string | null>(null);

  // A reload mid-journey should not send the applicant back to the code entry
  // screen: the server still holds the resolved client on the session.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getClientSessionApi();
        if (!cancelled && session?.clientId && session?.acceptedDocuments) {
          setClient({
            displayName: session.clientId,
            acceptedDocuments: session.acceptedDocuments,
            enableAdditionalDocuments: Boolean(session.enableAdditionalDocuments),
          });
          if (session.applicantEmail) setApplicantEmail(session.applicantEmail);
        }
      } catch {
        // No session yet is the normal first-visit case.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lookupCode = useCallback(async (code: string) => {
    setIsResolving(true);
    setError(null);
    try {
      const result = await lookupClientApi(code);

      if (result?.match === "tenant") {
        setIssuerChoice({
          tenantName: result.tenantName,
          issuers: result.issuers ?? [],
        });
        return;
      }

      setIssuerChoice(null);
      setClient({
        displayName: result.displayName,
        acceptedDocuments: result.acceptedDocuments ?? [],
        enableAdditionalDocuments: Boolean(result.enableAdditionalDocuments),
        branding: result.branding,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message;
      setError(
        status === 404
          ? "We could not find that code. Check it with whoever sent you this link."
          : status === 409
            ? // The organisation exists but is not set up; the server explains why.
              (serverMessage ?? "That organisation is not set up for verification yet.")
            : status === 429
              ? "Too many attempts. Please wait a moment and try again."
              : "We could not check that code right now. Please try again.",
      );
    } finally {
      setIsResolving(false);
    }
  }, []);

  const submitEmail = useCallback(async (email: string): Promise<boolean> => {
    setIsResolving(true);
    setError(null);
    try {
      const result = await setApplicantEmailApi(email);
      setApplicantEmail(result?.email ?? email);
      return true;
    } catch (err: any) {
      // The server does the checking, so show what it said rather than guessing.
      setError(
        err?.response?.data?.message ??
          "We could not save that email address right now. Please try again.",
      );
      return false;
    } finally {
      setIsResolving(false);
    }
  }, []);

  const cancelIssuerChoice = useCallback(() => {
    setIssuerChoice(null);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setClient(null);
    setIssuerChoice(null);
    setApplicantEmail(null);
    setError(null);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        client,
        issuerChoice,
        isResolving,
        error,
        lookupCode,
        cancelIssuerChoice,
        applicantEmail,
        submitEmail,
        reset,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = (): ClientContextType => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};
